// src/utils/cryptoCache.jsx
import { CACHE_KEY, CACHE_EXPIRY_KEY, CACHE_DURATION_MS } from "../constants";

/* Helpers */
function looksDecryptable(ivU8, cU8) {
  return (
    ivU8 instanceof Uint8Array &&
    ivU8.byteLength === 12 &&
    cU8 instanceof Uint8Array &&
    cU8.byteLength >= 16
  ); // incluye tag GCM
}

async function generateKey(password) {
  // console.log(
  //   "[cryptoCache] generateKey() - password length:",
  //   password.length
  // );

  const encodedPassword = new TextEncoder().encode(password ?? "");
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encodedPassword,
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: new TextEncoder().encode("unSaltUnico"),
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

async function encryptData(plain, password) {
  //console.log("[cryptoCache] encryptData() - plain length:", plain?.length);

  const key = await generateKey(password);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(plain)
  );
  return {
    encrypted: Array.from(new Uint8Array(encrypted)), // ← formato “actual”
    iv: Array.from(iv),
  };
}

async function decryptData(encrypted, password, iv) {
  const key = await generateKey(password);

  // Acepta ambos formatos: arrays o base64 (por si en otro lado migraste)
  const cipherU8 = Array.isArray(encrypted)
    ? new Uint8Array(encrypted)
    : new Uint8Array(0);
  const ivU8 = Array.isArray(iv) ? new Uint8Array(iv) : new Uint8Array(0);

  if (!looksDecryptable(ivU8, cipherU8)) {
    throw new Error("The provided data is too small");
  }

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: ivU8 },
    key,
    cipherU8
  );
  // console.log("[cryptoCache] decryptData() - OK", decrypted);

  return new TextDecoder().decode(decrypted);
}

function purge() {
  console.warn("[cryptoCache] PURGE localStorage keys", {
    CACHE_KEY,
    CACHE_EXPIRY_KEY,
  });

  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_EXPIRY_KEY);
  } catch (e) {
    console.error("[cryptoCache] purge() error:", e);
  }
}

export async function storeEncryptedCache(
  data,
  password,
  ttlMs = CACHE_DURATION_MS
) {
  try {
    const { encrypted, iv } = await encryptData(JSON.stringify(data), password);
    localStorage.setItem(CACHE_KEY, JSON.stringify({ encrypted, iv })); // ← unificado
    const exp =
      Date.now() +
      (Number.isFinite(ttlMs) && ttlMs > 0 ? ttlMs : CACHE_DURATION_MS);
    localStorage.setItem(CACHE_EXPIRY_KEY, String(exp));
  } catch (e) {
    console.error("Error cache store:", e);
  }
}

export async function readEncryptedCache(password) {
  try {
    const expiry = localStorage.getItem(CACHE_EXPIRY_KEY);
    if (expiry && Date.now() > Number(expiry)) {
      purge();
      return null;
    }
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    // Compat: acepta { encryptedData, iv } (legacy) o { encrypted, iv } (actual)
    const obj = JSON.parse(raw);
    const enc = Array.isArray(obj.encryptedData)
      ? obj.encryptedData
      : obj.encrypted;
    const iv = obj.iv;

    if (!Array.isArray(enc) || !Array.isArray(iv)) {
      purge();
      return null;
    }

    const plain = await decryptData(enc, password, iv);
    return JSON.parse(plain);
  } catch (e) {
    console.error("Error cache read:", e);
    purge(); // corrupción / claves diferentes → limpia para no seguir rompiendo
    return null;
  }
}
